import useNativeBack from '../hooks/useNativeBack';
import ConsumptionInputDemo from './ConsumptionInputDemo';
import SheetCloseButton from './SheetCloseButton';
import FormActions, { ActionButton } from './FormActions';
import { SheetPresence, SheetRoot } from './SheetMotion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Beer, Euro, ListChecks, Receipt, Sparkles } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import AppNavigationPreview from './AppNavigationPreview';

const ONBOARDING_VERSION = 'v1';
export const APP_ONBOARDING_REPLAY_EVENT = 'bb:replay-app-onboarding';

const slides = [
  {
    eyebrow: 'Welkom bij Cuplio',
    title: 'Samen halen, zonder gedoe',
    description: 'Kies je groep en bijeenkomst. Daarna houd je consumpties en betalingen samen bij.',
    icon: Sparkles,
    accent: 'from-violet-600 to-fuchsia-500',
    preview: <AppNavigationPreview />,
  },
  {
    eyebrow: '1 · Consumpties toevoegen',
    title: 'Jij haalt, de app telt',
    description: 'Controleer je bijeenkomst. Tik op een naam voor +1 en gebruik − voor één minder. Lege bekers staat onder de consumpties. Via Bekijken controleer je de bestelling; direct Opslaan kan ook.',
    icon: Beer,
    accent: 'from-primary to-fuchsia-600',
    preview: <ConsumptionInputDemo />,
  },
  {
    eyebrow: '2 · Overzicht',
    title: 'Altijd zicht op de stand',
    description: 'In Overzicht zie je per bijeenkomst wie wat heeft gehaald en wat je nog ontvangt. Via Meer vind je Afrekenen, Geschiedenis en je profiel.',
    icon: ListChecks,
    accent: 'from-primary to-fuchsia-600',
    preview: (
      <div className="overflow-hidden rounded-2xl bg-white text-zinc-900 shadow-xl ring-1 ring-zinc-200 dark:bg-zinc-950 dark:text-zinc-100 dark:ring-zinc-700">
        <div className="flex items-start justify-between border-b border-zinc-200 p-4 pb-3 dark:border-zinc-800"><strong className="text-lg">Sam</strong><strong className="text-xl text-green-500">+€ 15.00</strong></div>
        <div className="divide-y divide-zinc-200 text-sm dark:divide-zinc-800">
          <div className="flex justify-between bg-zinc-100 px-4 py-2 dark:bg-zinc-900/50"><span>Noa</span><strong className="text-green-500">+€ 10.00</strong></div>
          <div className="flex justify-between px-4 py-2"><span>Jesse</span><strong className="text-green-500">+€ 5.00</strong></div>
        </div>
        <div className="flex items-center justify-between p-4 pt-3"><div className="flex items-center gap-2 text-xs text-zinc-500"><span>Verrekend</span><span className="relative h-5 w-5 rounded-md border border-zinc-300"></span></div><span className="inline-flex items-center gap-1 text-sm font-medium text-primary"><Euro size={16} />Afrekenen</span></div>
      </div>
    ),
  },
  {
    eyebrow: '3 · Na de bijeenkomst',
    title: 'Samen afrekenen',
    description: 'Open Afrekenen vanuit Overzicht of via Meer. Controleer de bedragen en rond samen de betaling af — klaar voor de volgende bijeenkomst.',
    icon: Receipt,
    accent: 'from-primary to-fuchsia-600',
    preview: (
      <div className="space-y-3 rounded-2xl bg-white p-4 text-zinc-900 shadow-xl ring-1 ring-zinc-200 dark:bg-zinc-950 dark:text-zinc-100 dark:ring-zinc-700">
        <div className="flex items-center justify-between gap-2"><div><p className="text-xs uppercase tracking-wide text-zinc-500">Jouw kaart</p><p className="text-sm font-semibold">Sam</p></div><div className="text-right"><p className="text-xs text-zinc-500">Totaal nog te ontvangen</p><p className="text-lg font-bold text-green-500">€ 15.00</p></div></div>
        <div className="text-xs text-zinc-500">Wedstrijd: <strong className="text-zinc-900 dark:text-zinc-100">Heracles – PSV</strong></div>
        <div className="flex items-center justify-between pt-1"><span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">Naar overzicht <ArrowRight className="h-3.5 w-3.5" /></span><div className="flex items-center gap-2 text-xs text-zinc-500"><span>Verrekend</span><span className="relative h-5 w-5 rounded-md border border-zinc-300"></span></div></div>
      </div>
    ),
  },
];

const AppOnboarding = () => {
  const { user } = useUser();
  const { group } = useGroup();
  const navigate = useNavigate();
  const location = useLocation();
  const closeButtonRef = useRef(null);
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const storageKey = useMemo(() => user?.id && group?.id
    ? `bb_app_onboarding_${ONBOARDING_VERSION}_${user.id}_${group.id}`
    : null, [group?.id, user?.id]);

  useEffect(() => {
    if (!storageKey) return;
    setOpen(window.localStorage.getItem(storageKey) !== 'completed');
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    const params = new URLSearchParams(location.search);
    if (params.get('tour') !== '1') return;
    setStep(0);
    setOpen(true);
    params.delete('tour');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
  }, [storageKey, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    const replay = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener(APP_ONBOARDING_REPLAY_EVENT, replay);
    return () => window.removeEventListener(APP_ONBOARDING_REPLAY_EVENT, replay);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]);

  const complete = (destination) => {
    if (storageKey) window.localStorage.setItem(storageKey, 'completed');
    setOpen(false);
    if (destination) navigate(destination);
  };

  useNativeBack(open, () => complete());


  const slide = slides[step];
  const Icon = slide.icon;
  const isLast = step === slides.length - 1;

  return (
    <SheetPresence>{open && <SheetRoot className="sheet-tint sheet-tint-dark fixed inset-0 z-[1200] flex items-end justify-center bg-zinc-950/75 p-0 backdrop-blur-sm sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="app-onboarding-title">
      <section className="sheet-panel relative w-full max-w-lg max-h-[95dvh] overflow-y-auto rounded-t-[2rem] bg-white text-zinc-950 shadow-2xl dark:bg-zinc-900 dark:text-white sm:rounded-[2rem]">
        <div className={`bg-gradient-to-br ${slide.accent} px-6 pb-7 pt-6 text-white sm:px-8`}>
          <div className="flex items-center justify-between">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20"><Icon aria-hidden="true" /></span>
            <SheetCloseButton ref={closeButtonRef} onClick={() => complete()} inverted label="Onboarding overslaan" />
          </div>
          <div className="mt-6">{slide.preview}</div>
        </div>

        <div className="px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-6 sm:px-8 sm:pb-8">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-primary">{slide.eyebrow}</p>
          <h2 id="app-onboarding-title" className="mt-2 text-2xl font-black sm:text-3xl">{slide.title}</h2>
          <p className="mt-3 leading-relaxed text-zinc-600 dark:text-zinc-300">{slide.description}</p>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-1.5" aria-label={`Stap ${step + 1} van ${slides.length}`}>
              {slides.map((item, index) => <span key={item.title} className={`h-2 rounded-full transition-all ${index === step ? 'w-7 bg-primary' : 'w-2 bg-zinc-300 dark:bg-zinc-700'}`} />)}
            </div>
            <FormActions>
              <ActionButton onClick={() => isLast ? complete('/input') : setStep((current) => current + 1)}>
                {isLast ? 'Consumpties toevoegen' : 'Volgende'} {isLast ? <Beer size={19} /> : <ArrowRight size={19} />}
              </ActionButton>
              {step > 0 && <ActionButton variant="secondary" onClick={() => setStep((current) => current - 1)} aria-label="Vorige stap"><ArrowLeft /> Vorige stap</ActionButton>}
              {step === 0 && <ActionButton variant="secondary" onClick={() => complete('/input')}>Direct beginnen</ActionButton>}
            </FormActions>
          </div>
        </div>
      </section>
    </SheetRoot>}</SheetPresence>
  );
};

export default AppOnboarding;
